"use client";

import { useState } from "react";
import Link from "next/link";
import { ThemeToggle } from "@/components/theme-toggle";

// 타입 정의
interface AnalysisResult {
  summary: {
    skills: string[];
    experience: string;
    strength: string;
  };
  questions: Array<{
    question: string;
    intent: string;
  }>;
  report: {
    score: number;
    risk: string;
    fit: string;
  };
  classification?: {
    industry?: string;
    role?: string;
  };
  persona?: {
    traits: Array<{ trait: string; evidence: string }>;
    workStyle: string;
    interviewTips: string;
    caution?: string;
  };
}

export default function UploadPage() {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [analysisId, setAnalysisId] = useState<number | null>(null);
  const [qVotes, setQVotes] = useState<Record<number, "up" | "down">>({});
  const [outcome, setOutcome] = useState<string | null>(null);

  // 담당자 피드백 전송 (학습 신호)
  const sendFeedback = async (payload: Record<string, unknown>) => {
    if (!analysisId) return;
    try {
      await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analysisId, ...payload }),
      });
    } catch (e) {
      console.error("feedback 전송 실패", e);
    }
  };

  const voteQuestion = (idx: number, vote: "up" | "down") => {
    setQVotes((p) => ({ ...p, [idx]: vote }));
    sendFeedback({ kind: "question_vote", questionIndex: idx, vote });
  };

  const recordOutcome = (o: "interviewed" | "hired" | "rejected") => {
    setOutcome(o);
    sendFeedback({ kind: "outcome", outcome: o });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (uploadedFile: File) => {
    const name = uploadedFile.name.toLowerCase();
    const okExt = [".pdf", ".docx", ".hwpx", ".png", ".jpg", ".jpeg", ".webp", ".gif"];
    const isAllowed =
      uploadedFile.type === "application/pdf" ||
      uploadedFile.type.startsWith("image/") ||
      okExt.some((e) => name.endsWith(e));
    if (isAllowed) {
      setFile(uploadedFile);
      setResult(null); // 새 파일 올리면 결과 초기화
    } else {
      alert("PDF, DOCX, HWPX(한글) 또는 이미지(JPG/PNG) 파일을 올려 주세요.");
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setIsAnalyzing(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/api/analyze", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("분석 실패");
      }

      const data = await response.json();
      setResult(data);
      setAnalysisId(data?._meta?.analysisId ?? null);
      setQVotes({});
      setOutcome(null);
    } catch (error) {
      console.error(error);
      alert("분석 중 오류가 발생했습니다.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black flex flex-col font-sans transition-colors duration-300">
      {/* Header */}
      <header className="px-6 h-16 flex items-center bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <Link href="/" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mr-4 font-medium transition">
              ← Main
            </Link>
            <span className="font-bold text-lg text-gray-800 dark:text-white">
              SRHelper <span className="text-blue-600 dark:text-blue-400">Professional</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            {result && (
              <button 
                onClick={handleReset}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 underline"
              >
                새 분석 시작하기
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6 container mx-auto max-w-5xl">
        
        {/* Case 1: 결과가 없을 때 (업로드 화면) */}
        {!result && (
          <div className="max-w-xl mx-auto mt-10 bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 p-8 transition-all duration-300">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">인재 분석 리포트 생성</h2>
              <p className="text-gray-500 dark:text-gray-400">지원자의 이력서(PDF·DOCX·HWPX·이미지)를 업로드하면 AI가 심층 분석합니다.</p>
            </div>

            {!file ? (
              <div
                className={`border-2 border-dashed rounded-xl h-64 flex flex-col items-center justify-center transition-all cursor-pointer ${
                  isDragging
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-[1.02]"
                    : "border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("fileInput")?.click()}
              >
                <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-3xl mb-4 shadow-sm">
                  📤
                </div>
                <p className="text-gray-700 dark:text-gray-200 font-bold mb-1">이력서 파일 업로드</p>
                <p className="text-sm text-gray-400 dark:text-gray-500">PDF · DOCX · HWPX · 이미지(JPG/PNG) (최대 10MB)</p>
                <input
                  id="fileInput"
                  type="file"
                  accept=".pdf,.docx,.hwpx,.png,.jpg,.jpeg,.webp,.gif,image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-6 border border-blue-100 dark:border-blue-900/30">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center text-2xl shadow-sm mr-4">
                    📄
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white text-lg">{file.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB • 준비됨</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setFile(null)}
                    className="flex-1 py-3 px-4 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    disabled={isAnalyzing}
                  >
                    취소
                  </button>
                  <button
                    onClick={handleAnalyze}
                    className="flex-1 py-3 px-4 rounded-lg bg-blue-600 dark:bg-blue-500 text-white font-bold hover:bg-blue-700 dark:hover:bg-blue-600 shadow-md transition flex items-center justify-center gap-2"
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        분석 중...
                      </>
                    ) : (
                      "상세 분석 시작"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Case 2: 분석 결과 화면 (전문가 대시보드) */}
        {result && (
          <div className="animate-fade-in-up space-y-6">
            
            {/* 상단 요약 카드 */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* 점수 */}
              <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 flex flex-col items-center justify-center relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-cyan-400"></div>
                <h3 className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase tracking-wider mb-2">직무 적합도 점수</h3>
                <div className="text-5xl font-extrabold text-blue-600 dark:text-blue-400 mb-2">
                  {result.report.score}<span className="text-2xl text-gray-400 dark:text-gray-600">/100</span>
                </div>
                <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs px-2 py-1 rounded-full font-bold">Excellent Match</span>
              </div>

              {/* 핵심 역량 */}
              <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 md:col-span-2">
                <h3 className="text-gray-800 dark:text-white font-bold text-lg mb-4 flex items-center">
                  <span className="mr-2">⚡️</span> 핵심 역량 요약
                </h3>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {result.summary.skills.map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium border border-gray-200 dark:border-gray-700">
                        {skill}
                      </span>
                    ))}
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed border-l-4 border-blue-500 pl-3">
                    {result.summary.experience}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed border-l-4 border-green-500 pl-3">
                    <span className="font-bold text-gray-800 dark:text-white">강점:</span> {result.summary.strength}
                  </p>
                </div>
              </div>
            </div>

            {/* 리스크 & 상세 분석 */}
            <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
              <h3 className="text-gray-800 dark:text-white font-bold text-lg mb-4 flex items-center">
                <span className="mr-2">🕵️‍♂️</span> 인사이트 & 리스크 체크
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-yellow-50 dark:bg-yellow-900/10 p-4 rounded-xl border border-yellow-100 dark:border-yellow-900/30">
                  <h4 className="text-yellow-800 dark:text-yellow-400 font-bold mb-2 text-sm">⚠️ 주의 깊게 볼 점 (Risk)</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{result.report.risk}</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-900/30">
                  <h4 className="text-blue-800 dark:text-blue-400 font-bold mb-2 text-sm">💡 채용 의견 (Opinion)</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{result.report.fit}</p>
                </div>
              </div>
            </div>

            {/* 지원자 성향 (추정) */}
            {result.persona && (
              <div className="bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
                <div className="mb-5">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">🧭 추정 업무 성향</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">이력서 근거로 추정한 성향입니다(참고용). 면접에서 검증하세요.</p>
                </div>
                <div className="flex flex-wrap gap-2 mb-5">
                  {(result.persona.traits ?? []).map((t, i) => (
                    <div key={i} className="bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30 rounded-xl px-4 py-3">
                      <p className="text-purple-800 dark:text-purple-300 font-bold text-sm">{t.trait}</p>
                      <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">{t.evidence}</p>
                    </div>
                  ))}
                </div>
                {result.persona.workStyle && (
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-3"><span className="font-bold text-gray-800 dark:text-white">업무 성향:</span> {result.persona.workStyle}</p>
                )}
                <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-900/30 mb-2">
                  <h4 className="text-blue-800 dark:text-blue-400 font-bold mb-1 text-sm">🎤 성향 맞춤 면접 팁</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">{result.persona.interviewTips}</p>
                </div>
                {result.persona.caution && (
                  <div className="bg-amber-50 dark:bg-amber-900/10 p-4 rounded-xl border border-amber-100 dark:border-amber-900/30">
                    <h4 className="text-amber-800 dark:text-amber-500 font-bold mb-1 text-sm">⚠️ 성향상 주의점</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-sm">{result.persona.caution}</p>
                  </div>
                )}
              </div>
            )}

            {/* 채용 결과 피드백 (학습 신호) */}
            {analysisId && (
              <div className="bg-white dark:bg-gray-900 p-5 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800 flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">이 지원자에 대한 실제 진행 결과를 알려주시면 AI가 학습합니다:</span>
                {([
                  ["interviewed", "면접 진행"],
                  ["hired", "채용함"],
                  ["rejected", "부적합"],
                ] as const).map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => recordOutcome(val)}
                    className={`text-sm px-3 py-1.5 rounded-lg border font-medium transition ${outcome === val ? "bg-green-600 text-white border-green-600" : "border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                  >
                    {label}
                  </button>
                ))}
                {outcome && <span className="text-sm text-green-600 dark:text-green-400">✓ 반영되었습니다. 감사합니다!</span>}
              </div>
            )}

            {/* 면접 질문 생성기 */}
            <div className="bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800">
              <div className="flex justify-between items-end mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">🎯 생성된 맞춤 면접 질문</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">지원자의 경력을 검증하기 위해 AI가 설계한 심층 질문입니다.</p>
                </div>
                <button className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-black dark:hover:bg-gray-600 transition shadow-md">
                  질문 PDF 저장
                </button>
              </div>

              <div className="space-y-4">
                {result.questions.map((q, idx) => (
                  <div key={idx} className="group border border-gray-200 dark:border-gray-800 rounded-xl p-5 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md transition bg-gray-50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 dark:bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-4 text-sm">
                        Q{idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-900 dark:text-gray-100 font-bold text-lg mb-2">{q.question}</p>
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 inline-block px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700 group-hover:border-blue-200 dark:group-hover:border-blue-800 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition">
                          <span className="font-semibold mr-2">검증 의도:</span> {q.intent}
                        </div>
                        {analysisId && (
                          <div className="flex items-center gap-2 mt-3">
                            <span className="text-xs text-gray-400">이 질문이 유용한가요?</span>
                            <button
                              onClick={() => voteQuestion(idx, "up")}
                              className={`text-sm px-2 py-1 rounded-md border transition ${qVotes[idx] === "up" ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                            >
                              👍
                            </button>
                            <button
                              onClick={() => voteQuestion(idx, "down")}
                              className={`text-sm px-2 py-1 rounded-md border transition ${qVotes[idx] === "down" ? "bg-red-500 text-white border-red-500" : "border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"}`}
                            >
                              👎
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        )}
      </main>
    </div>
  );
}
